import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { YouTubeCreateConnect } from "@/components/YouTubeCreateConnect";
import {
  Sparkles, Loader2, Wand2, CheckCircle2, AlertTriangle, RefreshCw,
  ExternalLink, Lightbulb, ShieldCheck,
} from "lucide-react";

const inputCls = "bg-slate-900 border-slate-800 text-slate-100 focus-visible:ring-violet-500/30";
const REGEN = [
  { part: "name", label: "Name" },
  { part: "description", label: "Description" },
  { part: "pillars", label: "Pillars" },
  { part: "ideas", label: "Ideas" },
  { part: "style", label: "Style" },
];

export function CreateByAIModal({ open, onOpenChange, onCreated }) {
  const navigate = useNavigate();
  const [description, setDescription] = useState("");
  const [references, setReferences] = useState("");
  const [contentLanguage, setContentLanguage] = useState("");
  const [targetMarket, setTargetMarket] = useState("");
  const [scope, setScope] = useState("auto");
  const [busy, setBusy] = useState(false);
  const [regen, setRegen] = useState(null);
  const [result, setResult] = useState(null);
  const [showFullDesc, setShowFullDesc] = useState(false);

  const reset = () => {
    setDescription(""); setReferences(""); setContentLanguage("");
    setTargetMarket(""); setScope("auto"); setResult(null);
  };
  const close = (v) => { if (!v) { reset(); } onOpenChange(v); };

  const parseRefs = () =>
    references.split(/[\n,]+/).map((s) => s.trim()).filter(Boolean);

  const generate = async () => {
    if (!description.trim() && parseRefs().length === 0) {
      toast.error("Describe what you want, or add at least one reference channel.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/create-by-ai/generate", {
        description, references: parseRefs(),
        content_language: contentLanguage || null,
        target_market: targetMarket || null, scope,
      });
      setResult(data);
      onCreated?.(data.channel_id);
      toast.success("Profile ready");
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const regenerate = async (part) => {
    setRegen(part);
    try {
      const { data } = await api.post(`/create-by-ai/${result.channel_id}/regenerate`, { part });
      if (part === "ideas") {
        toast.success(`Added ${data.ideas_seeded} new idea(s)`);
        setResult((r) => ({ ...r, ideas_seeded: (r.ideas_seeded || 0) + (data.ideas_seeded || 0) }));
      } else if (part === "style") {
        toast.success("New style version created");
      } else {
        const { data: ch } = await api.get(`/channels/${result.channel_id}`);
        setResult((r) => ({
          ...r,
          identity: { ...r.identity, name: ch.data.name, description: ch.data.description, niche: ch.data.niche },
          content_pillars: (ch.data.editorial_instructions || "").split(" · ").filter(Boolean),
        }));
        toast.success(`${part} regenerated — other parts untouched`);
      }
    } catch (e) { toast.error(apiError(e)); }
    finally { setRegen(null); }
  };

  const identity = result?.identity || {};
  const refs = result?.references || [];

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="bg-[#111827] border-slate-700 text-slate-100 max-w-2xl max-h-[90vh] overflow-y-auto overflow-x-hidden" data-testid="create-ai-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display">
            <span className="w-8 h-8 rounded-lg bg-violet-600/90 flex items-center justify-center"><Wand2 className="w-4 h-4 text-white" /></span>
            Create channel with AI
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Describe your channel. The AI designs an original identity, editorial strategy and first ideas — an internal profile, not a live YouTube channel.
          </DialogDescription>
        </DialogHeader>

        {!result ? (
          <div className="space-y-4 min-w-0">
            <div>
              <Label className="text-slate-300 text-sm mb-1.5 block">What should this channel be about?</Label>
              <Textarea value={description} onChange={(e) => setDescription(e.target.value)}
                placeholder="e.g. Quick 60-second one-pan dinners for busy home cooks, upbeat and practical, US audience."
                className={`${inputCls} min-h-[96px]`} data-testid="create-ai-description" />
            </div>
            <div>
              <Label className="text-slate-300 text-sm mb-1.5 block flex items-center gap-1.5">
                Reference channels <span className="text-slate-500 font-normal">(optional — influence only)</span>
              </Label>
              <Textarea value={references} onChange={(e) => setReferences(e.target.value)}
                placeholder="One YouTube URL per line. We never copy them — and we honestly flag any we can't verify."
                className={`${inputCls} min-h-[64px]`} data-testid="create-ai-references" />
              <p className="text-[11px] text-slate-500 mt-1 flex items-center gap-1">
                <ShieldCheck className="w-3 h-3" /> References guide style only. Unverifiable links are flagged, never faked.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="create-by-ai-advanced">
              <div className="sm:col-span-3">
                <p className="text-[10px] uppercase tracking-wide text-slate-600 mb-1">Advanced overrides (optional)</p>
              </div>
              <div>
                <Label className="text-slate-300 text-xs mb-1 block">Content language</Label>
                <Input value={contentLanguage} onChange={(e) => setContentLanguage(e.target.value)}
                  placeholder="auto (e.g. en-US)" className={inputCls} data-testid="create-ai-language" />
              </div>
              <div>
                <Label className="text-slate-300 text-xs mb-1 block">Target market</Label>
                <Input value={targetMarket} onChange={(e) => setTargetMarket(e.target.value)}
                  placeholder="auto" className={inputCls} data-testid="create-ai-market" />
              </div>
              <div>
                <Label className="text-slate-300 text-xs mb-1 block">Scope</Label>
                <Select value={scope} onValueChange={setScope}>
                  <SelectTrigger className={inputCls} data-testid="create-ai-scope"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#111827] border-slate-700 text-slate-200">
                    <SelectItem value="auto">Auto</SelectItem>
                    <SelectItem value="narrow">Narrow</SelectItem>
                    <SelectItem value="broad">Broad</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" onClick={() => close(false)} className="border-slate-700 text-slate-300 hover:bg-slate-800" data-testid="create-ai-cancel">Cancel</Button>
              <Button onClick={generate} disabled={busy} className="bg-violet-600 hover:bg-violet-500 text-white" data-testid="create-ai-submit">
                {busy ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Designing…</> : <><Sparkles className="w-4 h-4 mr-1.5" /> Generate profile</>}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4 min-w-0" data-testid="create-ai-result">
            <div className="flex items-center gap-2 text-emerald-300 text-sm font-medium">
              <CheckCircle2 className="w-4 h-4" /> {result.status}
              <span className="text-[11px] px-2 py-0.5 rounded-full bg-violet-500/10 text-violet-300 border border-violet-500/30">origin: create-by-ai</span>
            </div>

            <div className="border border-slate-800 rounded-xl p-4 bg-slate-900/40 min-w-0">
              <h3 className="text-lg font-display font-bold text-slate-50 break-words" data-testid="create-ai-name">{identity.name}</h3>
              {identity.niche && <p className="text-sm text-violet-300 mt-0.5 break-words">{identity.niche}</p>}
              {result.handle_suggestion && <p className="text-xs text-slate-500 mt-0.5 break-words">Suggested handle: <span className="text-slate-300">{result.handle_suggestion}</span></p>}
              {identity.target_audience && <p className="text-xs text-slate-400 mt-2 break-words">Audience: {identity.target_audience}</p>}
              {identity.description && (
                <>
                  <p className={`text-sm text-slate-300 mt-2 whitespace-pre-wrap break-words ${showFullDesc ? "" : "line-clamp-4"}`} style={{ overflowWrap: "anywhere" }}>{identity.description}</p>
                  {identity.description.length > 220 && (
                    <button onClick={() => setShowFullDesc((v) => !v)} className="text-[11px] text-violet-300 hover:text-violet-200 mt-1" data-testid="create-ai-desc-toggle">{showFullDesc ? "Show less" : "Show more"}</button>
                  )}
                </>
              )}

              {(result.content_pillars || []).length > 0 && (
                <div className="mt-3">
                  <p className="text-[10px] uppercase tracking-wide text-slate-600 mb-1.5">Content pillars</p>
                  <div className="flex flex-wrap gap-1.5 min-w-0" data-testid="create-ai-pillars">
                    {result.content_pillars.map((p, i) => (
                      <span key={`${p}-${i}`} className="text-xs px-2 py-0.5 rounded-full bg-slate-800 text-slate-300 border border-slate-700 min-w-0 break-words" style={{ overflowWrap: "anywhere" }}>{p}</span>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex flex-wrap items-center gap-2 mt-3">
                <span className="text-xs px-2 py-0.5 rounded-full bg-slate-800 text-slate-300 border border-slate-700 flex items-center gap-1"><Lightbulb className="w-3 h-3" /> {result.ideas_seeded} ideas seeded</span>
                {result.brand_state === "pending" && (
                  <span className="text-xs px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-300 border border-amber-500/30">Brand assets pending</span>
                )}
              </div>
            </div>

            {refs.length > 0 && (
              <div className="border border-slate-800 rounded-xl p-3 bg-slate-900/40">
                <p className="text-[10px] uppercase tracking-wide text-slate-600 mb-2">Reference honesty</p>
                <div className="space-y-1.5" data-testid="create-ai-references-result">
                  {refs.map((r, i) => (
                    <div key={r.url || i} className="flex items-start gap-2 text-xs">
                      {r.verified
                        ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 mt-0.5 flex-none" />
                        : <AlertTriangle className="w-3.5 h-3.5 text-amber-400 mt-0.5 flex-none" />}
                      <div className="min-w-0">
                        <p className="text-slate-300 break-all">{r.url}</p>
                        <p className={r.verified ? "text-emerald-400/80 break-words" : "text-amber-400/80 break-words"}>{r.note}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div>
              <p className="text-[10px] uppercase tracking-wide text-slate-600 mb-1.5">Regenerate a part (your other edits survive)</p>
              <div className="flex flex-wrap gap-2">
                {REGEN.map((r) => (
                  <Button key={r.part} size="sm" variant="outline" disabled={!!regen}
                    onClick={() => regenerate(r.part)} data-testid={`create-ai-regen-${r.part}`}
                    className="border-slate-700 text-slate-300 hover:bg-slate-800 h-8">
                    {regen === r.part ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1" />}
                    {r.label}
                  </Button>
                ))}
              </div>
            </div>

            <p className="text-[11px] text-slate-500">{result.note}</p>

            <YouTubeCreateConnect
              channelId={result.channel_id}
              name={identity.name}
              handle={result.handle_suggestion}
              description={identity.description}
            />

            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" onClick={() => reset()} className="border-slate-700 text-slate-300 hover:bg-slate-800" data-testid="create-ai-another">Create another</Button>
              <Button onClick={() => { navigate(`/channels/${result.channel_id}`); close(false); }} className="bg-violet-600 hover:bg-violet-500 text-white" data-testid="create-ai-open-channel">
                <ExternalLink className="w-4 h-4 mr-1.5" /> Open channel
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
